// Pure lookup logic for the site-wide translator. Catalog keys are the English text;
// {0}, {1}... stand for values such as numbers or names.
export type Catalog = Record<string, string>;
export interface CompiledCatalog {
  exact: Map<string, string>;
  patterns: { regex: RegExp; target: string }[];
}

const escapeRegex = (text: string) => text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
export const normalize = (text: string) => text.replace(/\s+/g, ' ').trim();

export function compileCatalog(catalog: Catalog): CompiledCatalog {
  const exact = new Map<string, string>();
  const patterns: CompiledCatalog['patterns'] = [];
  for (const [source, target] of Object.entries(catalog)) {
    if (!/\{\d\}/.test(source)) { exact.set(source, target); continue; }
    const order: number[] = [];
    const body = source.split(/(\{\d\})/).map((part) => {
      const slot = part.match(/^\{(\d)\}$/);
      if (!slot) return escapeRegex(part);
      order.push(Number(slot[1]));
      return '(.*?)';
    }).join('');
    const slots = order.reduce<Record<number, number>>((map, slot, index) => ({ ...map, [slot]: index + 1 }), {});
    const regex = new RegExp(`^${body}$`);
    // Longer patterns are more specific, so they get the first chance to match.
    patterns.push({ regex, target: target.replace(/\{(\d)\}/g, (_all, slot: string) => `\uE000${slots[Number(slot)]}\uE000`) });
  }
  patterns.sort((a, b) => b.regex.source.length - a.regex.source.length);
  return { exact, patterns };
}

// Returns the translation, or null when the catalog has nothing for this text.
export function translateText(text: string, catalog: CompiledCatalog, depth = 0): string | null {
  const key = normalize(text);
  if (!key || !/[A-Za-z]/.test(key)) return null;
  const direct = catalog.exact.get(key);
  if (direct !== undefined) return keepEdgeSpace(text, direct);
  if (depth > 2) return null;
  for (const { regex, target } of catalog.patterns) {
    const match = key.match(regex);
    if (!match) continue;
    const filled = target.replace(/\uE000(\d)\uE000/g, (_all, group: string) => {
      const value = match[Number(group)] ?? '';
      return translateText(value, catalog, depth + 1) ?? value;
    });
    return keepEdgeSpace(text, filled);
  }
  return null;
}

// "Pay " next to a bold amount must keep its space, or the words run together.
function keepEdgeSpace(original: string, translated: string) {
  const lead = /^\s/.test(original) ? ' ' : '';
  const trail = /\s$/.test(original) ? ' ' : '';
  return `${lead}${translated}${trail}`;
}
